import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import type { TournamentItems, CardTournamentItem, TournamentItem, Leaderboard, Filters, GameItems, Game, ProviderItems, Provider, NewTournament, OngoingTournament, TournamentSocketObject } from '@/types';
import createQueryString from '@/utils/queryString';
import { appendItems } from '@/utils/appendItems';
import { getAuthHeaders } from '@/utils/getHeaders';
import { storageDomain } from '@/utils/variables';
import { usePlayerStore } from './player';

export const useTournamentsStore = defineStore('tournaments', () => {
    const playerStore = usePlayerStore();
    const isPlayer = computed(() => playerStore.isPlayer);
    const myTournaments = ref<TournamentItems>({
        items: [] as CardTournamentItem[],
        total: 0,
        size: 0,
        page: 0,
        pages: 0
    });
    const availableTournaments = ref<TournamentItems>({
        items: [] as CardTournamentItem[],
        total: 0,
        size: 0,
        page: 0,
        pages: 0
    });
    const availableGames = ref<GameItems>({
        items: [] as Game[],
        total: 0,
        size: 0,
        page: 0,
        pages: 0
    });
    const availableProviders = ref<ProviderItems>({
        items: [] as Provider[],
        total: 0,
        size: 0,
        page: 0,
        pages: 0
    });
    const tournament = ref<TournamentItem | null>(null);
    const completedTournament = ref<TournamentItem | null>(null);
    const newTournament = ref<NewTournament>({
        tournament: null,
        tournament_id: null,
        trigger: false
    });
    const ongoingTournament = ref<OngoingTournament>({
        tournament: null,
        tournament_id: null,
        trigger: false
    });
    const tournamentOngoingTrigger = ref<boolean>(false);
    const tournamentCompletedTrigger = ref<boolean>(false);
    const tournamentLoading = ref<{
        my: boolean;
        available: boolean;
    }>({
        my: false,
        available: false
    });
    function toResetTournaments() {
        myTournaments.value = {
            items: [],
            total: 0,
            size: 0,
            page: 0,
            pages: 0
        };
        availableTournaments.value = {
            items: [],
            total: 0,
            size: 0,
            page: 0,
            pages: 0
        };
        tournament.value = null;
        completedTournament.value = null;
        newTournament.value = {
            tournament: null,
            tournament_id: null,
            trigger: false
        };
        ongoingTournament.value = {
            tournament: null,
            tournament_id: null,
            trigger: false
        };
        tournamentOngoingTrigger.value = false;
        tournamentCompletedTrigger.value = false;
    }
    function toSetTournamentCompletedTrigger(flag: boolean) {
        tournamentCompletedTrigger.value = flag;
    }
    function toSetNewTournament(flag: boolean, tournament?: TournamentItem | null, id?: number) {
        newTournament.value.trigger = flag;
        if (id) {
            newTournament.value.tournament_id = id;
        }
        if (tournament) {
            newTournament.value.tournament = tournament;
        }
    }
    function toSetTournamentOngoingTrigger(flag: boolean, tournament?: TournamentSocketObject | null) {
        ongoingTournament.value.trigger = flag;
        if (tournament) {
            ongoingTournament.value.tournament = {
                ...tournament,
                loadingItem: false
            };
        }
    }
    function toUpdateTournament(tournamentObject: TournamentSocketObject) {
        let tournamentItem;
        if (tournamentObject.is_user_auto_subscribe_enabled) {
            tournamentItem = myTournaments.value.items.find((item) => item.id === tournamentObject.id);
        } else {
            tournamentItem = availableTournaments.value.items.find((item) => item.id === tournamentObject.id);
        }
        if (tournamentItem) {
            tournamentItem.status = tournamentObject.status;
        }
        if (tournamentObject.id === tournament.value?.id) {
            tournament.value.status = tournamentObject.status;
            tournament.value.leaderboard_position = tournamentObject.leaderboard_position;
            tournament.value.final_prize_amount = tournamentObject.final_prize_amount;
        }
    }
    function toSetTournamentFlip(tournament: CardTournamentItem, flag: boolean, list: 'my' | 'available') {
        const index = list === 'my' ? myTournaments.value.items.findIndex((item) => item.id === tournament.id) : availableTournaments.value.items.findIndex((item) => item.id === tournament.id);
        if (index !== undefined && index >= 0) {
            if (list === 'my') {
                myTournaments.value.items[index].flip = flag;
            } else {
                availableTournaments.value.items[index].flip = flag;
            }
        }
    }
    function toBindTournament(currentTournament: TournamentItem, sourceTournament: TournamentItem | null) {
        if (currentTournament.id === sourceTournament?.id) {
            sourceTournament = currentTournament;
        }
    }
    const awaitGetAvailableGames = async (filters: Filters) => {
        try {
            const queryString = createQueryString(filters);
            const headers = isPlayer.value ? getAuthHeaders() : {};
            const response = await axios.get<GameItems>(`/main-api/games${isPlayer.value ? '/my' : ''}/filter${queryString}`, headers);
            if (response.status === 200) {
                const rd = {
                    items: response.data.items,
                    total: response.data.total,
                    size: response.data.size,
                    page: response.data.page,
                    pages: response.data.pages
                };
                if (filters.page === 1) {
                    availableGames.value = rd;
                } else {
                    appendItems(availableGames, rd);
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetAvailableProviders = async (filters: Filters) => {
        try {
            const queryString = createQueryString(filters);
            const headers = isPlayer.value ? getAuthHeaders() : {};
            const response = await axios.get<ProviderItems>(`/main-api/games${isPlayer.value ? '/my' : ''}/providers${queryString}`, headers);
            if (response.status === 200) {
                const rd = {
                    items: response.data.items,
                    total: response.data.total,
                    size: response.data.size,
                    page: response.data.page,
                    pages: response.data.pages
                };
                if (filters.page === 1) {
                    availableProviders.value = rd;
                } else {
                    appendItems(availableProviders, rd);
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetLeaderboard = async (id: number, filters: Filters) => {
        try {
            const queryString = createQueryString(filters);
            const response = await axios.get<Leaderboard>(`/main-api/tournaments/${id}/leaderboard${queryString}`, getAuthHeaders());
            if (response.status === 200 && tournament.value) {
                const rd = response.data;
                const items = rd.items.map((item) => ({
                    ...item,
                    avatar: {
                        image: `https://${storageDomain}/avatars/${item.avatar.image}`,
                        preview: `https://${storageDomain}/avatars/${item.avatar.preview}`
                    }
                }));
                const data = {
                    ...rd,
                    items: items
                };
                tournament.value.leaderboard = data;
                const availableTournament = availableTournaments.value.items.find((item) => item.id === id);
                if (availableTournament) {
                    availableTournament.leaderboard = data;
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetTournament = async (id: number, isNew?: boolean) => {
        try {
            const response = await axios.get<TournamentItem>(`/main-api/tournaments/${id}`, getAuthHeaders());
            if (response.status === 200) {
                const rd = {
                    ...response.data,
                    loadingItem: false
                };
                if (isNew) {
                    newTournament.value.tournament = rd;
                } else {
                    tournament.value = rd;
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetCompletedTournament = async (id: number) => {
        try {
            const response = await axios.get<TournamentItem>(`/main-api/tournaments/${id}`, getAuthHeaders());
            toSetTournamentCompletedTrigger(true);
            if (response.status === 200) {
                const rd = {
                    ...response.data,
                    loadingItem: false
                };
                completedTournament.value = rd;
                if (tournament.value && tournament.value.id === completedTournament.value.id) {
                    tournament.value.status = 'completed';
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetMyTournaments = async () => {
        tournamentLoading.value.my = true;
        try {
            const response = await axios.get<TournamentItems>('/main-api/tournaments/my', getAuthHeaders());
            if (response.status === 200) {
                const rd = response.data.items.map((item) => ({
                    ...item,
                    loadingItem: false,
                    flip: false
                })) as CardTournamentItem[];
                myTournaments.value = {
                    ...response.data,
                    items: rd
                };
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            tournamentLoading.value.my = false;
        }
    };
    const awaitGetAvailableTournaments = async (newTournamentId?: number | null) => {
        tournamentLoading.value.available = true;
        try {
            const response = await axios.get<TournamentItems>('/main-api/tournaments/available', getAuthHeaders());
            if (response.status === 200) {
                const rd = response.data.items.map((item) => ({
                    ...item,
                    loadingItem: false,
                    isNewTournament: newTournamentId === item.id,
                    flip: false
                })) as CardTournamentItem[];
                availableTournaments.value = {
                    ...response.data,
                    items: rd
                };
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            tournamentLoading.value.available = false;
        }
    };
    const awaitJoinTournament = async (tournament_id: number) => {
        try {
            await axios.post(`/main-api/tournaments/${tournament_id}/join`, null, getAuthHeaders());
            if (myTournaments.value.items.length > 0 || availableTournaments.value.items.length > 0) {
                awaitGetMyTournaments();
                awaitGetAvailableTournaments();
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitLeaveTournament = async (tournament_id: number) => {
        try {
            await axios.delete(`/main-api/tournaments/${tournament_id}/unsubscribe`, getAuthHeaders());
            if (myTournaments.value.items.length > 0 || availableTournaments.value.items.length > 0) {
                awaitGetMyTournaments();
                awaitGetAvailableTournaments();
            }
            if (tournament.value?.participation_id) {
                tournament.value.participation_id = null;
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    return {
        toResetTournaments,
        tournamentLoading,
        tournamentCompletedTrigger,
        toSetTournamentOngoingTrigger,
        tournamentOngoingTrigger,
        toBindTournament,
        ongoingTournament,
        toUpdateTournament,
        toSetNewTournament,
        newTournament,
        toSetTournamentCompletedTrigger,
        awaitGetCompletedTournament,
        completedTournament,
        awaitGetLeaderboard,
        awaitGetTournament,
        tournament,
        toSetTournamentFlip,
        myTournaments,
        awaitGetMyTournaments,
        availableTournaments,
        awaitGetAvailableTournaments,
        awaitJoinTournament,
        awaitLeaveTournament,
        availableGames,
        availableProviders,
        awaitGetAvailableGames,
        awaitGetAvailableProviders
    };
});
